import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEmail, useCreateEmail, useUpdateEmail } from '../../hooks/useEmails';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import { Badge, Button, Card, CardHeader, Input } from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import { X } from 'lucide-react';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LOCAL_PART_REGEX = /^[a-zA-Z0-9._-]{1,64}$/;

export default function EmailForm() {
  const { emailId } = useParams<{ emailId: string }>();
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();

  const isEditing = !!emailId;

  const { data: existingEmail, isLoading: emailLoading } = useEmail(emailId);
  const createEmail = useCreateEmail();
  const updateEmail = useUpdateEmail();

  const [localPart, setLocalPart] = useState('');
  const [allowedSenders, setAllowedSenders] = useState<string[]>([]);
  const [senderInput, setSenderInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (existingEmail && isEditing) {
      setAllowedSenders(existingEmail.allowedSenders || []);
    }
  }, [existingEmail, isEditing]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!isEditing && localPart) {
      if (!LOCAL_PART_REGEX.test(localPart)) {
        newErrors.localPart =
          'Must be 1-64 characters: letters, numbers, dots, hyphens, underscores';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddSender = () => {
    const trimmed = senderInput.trim();
    if (!trimmed) return;

    if (!EMAIL_REGEX.test(trimmed)) {
      setErrors((prev) => ({ ...prev, sender: 'Invalid email format' }));
      return;
    }

    if (allowedSenders.includes(trimmed)) {
      setErrors((prev) => ({ ...prev, sender: 'Already added' }));
      return;
    }

    setAllowedSenders((prev) => [...prev, trimmed]);
    setSenderInput('');
    setErrors((prev) => {
      const { sender: _, ...rest } = prev;
      return rest;
    });
  };

  const handleRemoveSender = (sender: string) => {
    setAllowedSenders((prev) => prev.filter((s) => s !== sender));
  };

  const handleSenderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSender();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      if (isEditing && emailId) {
        await updateEmail.mutateAsync({
          emailId,
          input: { allowedSenders },
        });
        success('Email address updated successfully');
        navigate(routes.console.spaceConnectionEmail(spaceId, emailId!));
      } else {
        const input: { localPart?: string; allowedSenders?: string[] } = {};
        if (localPart.trim()) {
          input.localPart = localPart.trim();
        }
        if (allowedSenders.length > 0) {
          input.allowedSenders = allowedSenders;
        }
        const newEmail = await createEmail.mutateAsync(input);
        success('Email address created successfully');
        navigate(routes.console.spaceConnectionEmail(spaceId, newEmail.emailId));
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save email address');
    }
  };

  if (isEditing && emailLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const isSubmitting = createEmail.isPending || updateEmail.isPending;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {isEditing ? 'Edit Email Address' : 'Create Email Address'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isEditing
            ? 'Update the allowed senders for this email address'
            : 'Create a new email address for knowledge base ingestion'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email address */}
        <Card>
          <CardHeader
            title="Email Address"
            description={
              isEditing
                ? 'The email address cannot be changed after creation'
                : 'Optionally specify a local part, or leave blank to auto-generate'
            }
          />
          <div className="space-y-4">
            {isEditing ? (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Email Address
                </label>
                <code className="text-sm bg-muted px-3 py-2 rounded-md block font-mono text-muted-foreground">
                  {existingEmail?.emailAddress}
                </code>
              </div>
            ) : (
              <Input
                label="Local Part"
                value={localPart}
                onChange={(e: { target: { value: string } }) => setLocalPart(e.target.value)}
                error={errors.localPart}
                placeholder="auto-generated"
                hint="Optional — becomes {localPart}@kb-stg.optr.ai. Leave blank to auto-generate."
              />
            )}
          </div>
        </Card>

        {/* Allowed senders */}
        <Card>
          <CardHeader
            title="Allowed Senders"
            description="Restrict which email addresses can send to this inbox. Leave empty to accept all senders."
          />
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  placeholder="sender@example.com"
                  value={senderInput}
                  onChange={(e: { target: { value: string } }) => {
                    setSenderInput(e.target.value);
                    setErrors((prev) => {
                      const { sender: _, ...rest } = prev;
                      return rest;
                    });
                  }}
                  onKeyDown={handleSenderKeyDown}
                  error={errors.sender}
                />
              </div>
              <Button type="button" variant="secondary" onClick={handleAddSender}>
                Add
              </Button>
            </div>

            {allowedSenders.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {allowedSenders.map((sender) => (
                  <Badge key={sender} variant="gray" className="flex items-center gap-1 pr-1">
                    {sender}
                    <button
                      type="button"
                      onClick={() => handleRemoveSender(sender)}
                      className="ml-1 rounded-full p-0.5 hover:bg-accent transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Link to={isEditing ? routes.console.spaceConnectionEmail(spaceId, emailId!) : routes.console.spaceConnectionEmails(spaceId)}>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Link>
          <Button type="submit" isLoading={isSubmitting}>
            {isEditing ? 'Save Changes' : 'Create Email'}
          </Button>
        </div>
      </form>
    </div>
  );
}
